import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import accessControlService from '../services/accessControlService';
import {
  Shield, Key, FileText, Activity, Plus, Edit, Trash2,
  Search, Filter, CheckCircle, XCircle, Eye, Lock, Unlock,
  Users, Settings, AlertCircle, TrendingUp, Clock, X, Download
} from 'lucide-react';
import { FilterSection, useToast, LoadingState, EmptyState, SmartDropdown } from '../components/ui';

const AccessControl = () => {
  const [activeTab, setActiveTab] = useState('policies');
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    user: 'all',
    actionType: 'all',
    status: 'all',
    dateRange: '7days'
  });
  const [formData, setFormData] = useState({});

  const queryClient = useQueryClient();
  const { success, error: showError } = useToast();

  // Queries
  const { data: policies = [], isLoading: policiesLoading } = useQuery({
    queryKey: ['access-policies'],
    queryFn: () => accessControlService.getAccessPolicies()
  });

  const { data: permissionGroups = [], isLoading: permissionsLoading } = useQuery({
    queryKey: ['permission-groups'],
    queryFn: () => accessControlService.getPermissionGroups()
  });

  const { data: auditLogs = [], isLoading: logsLoading } = useQuery({
    queryKey: ['audit-logs'],
    queryFn: () => accessControlService.getRecentAuditLogs(100)
  });

  const { data: roles = [] } = useQuery({
    queryKey: ['roles'],
    queryFn: () => accessControlService.getRoles()
  });

  const { data: policyStats } = useQuery({
    queryKey: ['policy-stats'],
    queryFn: () => accessControlService.getAccessPolicyStats()
  });

  const { data: auditStats } = useQuery({
    queryKey: ['audit-stats'],
    queryFn: () => accessControlService.getAuditLogStats()
  });

  // Mutations for Access Policies
  const createPolicyMutation = useMutation({
    mutationFn: (data) => accessControlService.createAccessPolicy(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['access-policies']);
      queryClient.invalidateQueries(['policy-stats']);
      success('Access policy created successfully!');
      closeModal();
    },
    onError: (err) => {
      showError(err.response?.data?.message || 'Failed to create policy');
    }
  });

  const updatePolicyMutation = useMutation({
    mutationFn: ({ id, data }) => accessControlService.updateAccessPolicy(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['access-policies']);
      success('Access policy updated successfully!');
      closeModal();
    },
    onError: (err) => {
      showError(err.response?.data?.message || 'Failed to update policy');
    }
  });

  const deletePolicyMutation = useMutation({
    mutationFn: (id) => accessControlService.deleteAccessPolicy(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['access-policies']);
      queryClient.invalidateQueries(['policy-stats']);
      success('Access policy deleted successfully!');
      closeModal();
    },
    onError: (err) => {
      showError(err.response?.data?.message || 'Failed to delete policy');
    }
  });

  // Mutations for Permission Groups
  const createPermissionMutation = useMutation({
    mutationFn: (data) => accessControlService.createPermissionGroup(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['permission-groups']);
      success('Permission group created successfully!');
      closeModal();
    },
    onError: (err) => {
      showError(err.response?.data?.message || 'Failed to create permission group');
    }
  });

  const updatePermissionMutation = useMutation({
    mutationFn: ({ id, data }) => accessControlService.updatePermissionGroup(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['permission-groups']);
      success('Permission group updated successfully!');
      closeModal();
    },
    onError: (err) => {
      showError(err.response?.data?.message || 'Failed to update permission group');
    }
  });

  const deletePermissionMutation = useMutation({
    mutationFn: (id) => accessControlService.deletePermissionGroup(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['permission-groups']);
      success('Permission group deleted successfully!');
      closeModal();
    },
    onError: (err) => {
      showError(err.response?.data?.message || 'Failed to delete permission group');
    }
  });

  // Helper functions
  const openModal = (type, item = null) => {
    setModalType(type);
    setSelectedItem(item);
    setShowModal(true);

    if (item) {
      setFormData(item);
    } else {
      setFormData({});
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setModalType('');
    setSelectedItem(null);
    setFormData({});
  };

  // Export audit logs to CSV
  const handleExportLogs = () => {
    try {
      const csvHeaders = ['Timestamp', 'User', 'Action', 'Resource Type', 'Resource ID', 'Description', 'IP Address', 'Status'];
      const csvRows = filteredLogs.map(log => [
        formatDate(log.created_at),
        log.username || 'Unknown',
        log.action_type,
        log.resource_type,
        log.resource_id || 'N/A',
        log.description,
        log.ip_address || 'N/A',
        log.status
      ]);

      const csvContent = [
        csvHeaders.join(','),
        ...csvRows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.setAttribute('hidden', '');
      a.setAttribute('href', url);
      a.setAttribute('download', `audit_logs_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      success('Audit logs exported successfully!');
    } catch (err) {
      showError('Failed to export audit logs');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (modalType === 'addPolicy') {
      createPolicyMutation.mutate(formData);
    } else if (modalType === 'editPolicy') {
      updatePolicyMutation.mutate({ id: selectedItem.id, data: formData });
    } else if (modalType === 'addPermission') {
      createPermissionMutation.mutate(formData);
    } else if (modalType === 'editPermission') {
      updatePermissionMutation.mutate({ id: selectedItem.id, data: formData });
    }
  };

  const handleDelete = () => {
    if (modalType === 'deletePolicy') {
      deletePolicyMutation.mutate(selectedItem.id);
    } else if (modalType === 'deletePermission') {
      deletePermissionMutation.mutate(selectedItem.id);
    }
  };

  const getResourceTypeBadge = (type) => {
    const colors = {
      gate: 'bg-blue-100 text-blue-800',
      dock: 'bg-green-100 text-green-800',
      warehouse: 'bg-purple-100 text-purple-800',
      qc: 'bg-yellow-100 text-yellow-800',
      reports: 'bg-indigo-100 text-indigo-800',
      dashboard: 'bg-pink-100 text-pink-800',
      management: 'bg-red-100 text-red-800',
      workflow: 'bg-orange-100 text-orange-800',
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const getStatusBadge = (status) => {
    const colors = {
      success: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      denied: 'bg-yellow-100 text-yellow-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Tab definitions
  const tabs = [
    { id: 'policies', name: 'Access Policies', icon: Shield, count: policies.length },
    { id: 'permissions', name: 'Permission Groups', icon: Key, count: permissionGroups.length },
    { id: 'audit', name: 'Audit Logs', icon: FileText, count: auditLogs.length },
    { id: 'overview', name: 'Overview', icon: Activity },
  ];

  // Filter data based on filters
  const filteredPolicies = policies.filter(policy =>
    (!filters.search ||
      policy.name.toLowerCase().includes(filters.search.toLowerCase()) ||
      policy.description.toLowerCase().includes(filters.search.toLowerCase()))
  );

  const filteredPermissions = permissionGroups.filter(group =>
    (!filters.search ||
      group.name.toLowerCase().includes(filters.search.toLowerCase()) ||
      group.module.toLowerCase().includes(filters.search.toLowerCase()))
  );

  const filteredLogs = auditLogs.filter(log => {
    const matchesSearch = !filters.search ||
      log.description.toLowerCase().includes(filters.search.toLowerCase()) ||
      log.username?.toLowerCase().includes(filters.search.toLowerCase());

    const matchesUser = filters.user === 'all' || log.username === filters.user;
    const matchesAction = filters.actionType === 'all' || log.action_type === filters.actionType;
    const matchesStatus = filters.status === 'all' || log.status === filters.status;

    // Date range filter
    let matchesDate = true;
    if (filters.dateRange !== 'all' && filters.dateRange !== '7days') {
      const logDate = new Date(log.created_at);
      const now = new Date();
      const daysAgo = parseInt(filters.dateRange.replace('days', ''));
      const cutoffDate = new Date(now.getTime() - (daysAgo * 24 * 60 * 60 * 1000));
      matchesDate = logDate >= cutoffDate;
    }

    return matchesSearch && matchesUser && matchesAction && matchesStatus && matchesDate;
  });

  // Get unique users for filter
  const uniqueUsers = [...new Set(auditLogs.map(log => log.username).filter(Boolean))];
  const uniqueActions = [...new Set(auditLogs.map(log => log.action_type).filter(Boolean))];

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <Shield className="w-8 h-8 text-blue-600" />
              Access Control
            </h1>
            <p className="text-gray-600 mt-1">Manage permissions, policies, and audit system access</p>
          </div>
        </div>
      </div>


      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="flex border-b border-gray-200 overflow-x-auto">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-4 font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                <Icon className="w-5 h-5" />
                {tab.name}
                {tab.count !== undefined && (
                  <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full text-xs">
                    {tab.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Search and Actions */}
      {activeTab !== 'overview' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between gap-4">
              <div className="flex-1">
                {activeTab === 'audit' ? (
                  <FilterSection
                    filters={[
                      {
                        name: 'user',
                        label: 'User',
                        value: filters.user,
                        options: [
                          { value: 'all', label: 'All Users' },
                          ...uniqueUsers.map(user => ({ value: user, label: user }))
                        ]
                      },
                      {
                        name: 'actionType',
                        label: 'Action Type',
                        value: filters.actionType,
                        options: [
                          { value: 'all', label: 'All Actions' },
                          ...uniqueActions.map(action => ({ value: action, label: action.toUpperCase() }))
                        ]
                      },
                      {
                        name: 'status',
                        label: 'Status',
                        value: filters.status,
                        options: [
                          { value: 'all', label: 'All Statuses' },
                          { value: 'success', label: 'Success' },
                          { value: 'failed', label: 'Failed' },
                          { value: 'denied', label: 'Denied' }
                        ]
                      },
                      {
                        name: 'dateRange',
                        label: 'Date Range',
                        value: filters.dateRange,
                        options: [
                          { value: '7days', label: 'Last 7 Days' },
                          { value: '14days', label: 'Last 14 Days' },
                          { value: '30days', label: 'Last 30 Days' },
                          { value: '90days', label: 'Last 90 Days' },
                          { value: 'all', label: 'All Time' }
                        ]
                      }
                    ]}
                    onFilterChange={(name, value) => setFilters(prev => ({ ...prev, [name]: value }))}
                    onClearFilters={() => setFilters({ search: '', user: 'all', actionType: 'all', status: 'all', dateRange: '7days' })}
                    searchValue={filters.search}
                    searchPlaceholder="Search audit logs..."
                  />
                ) : (
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      placeholder={`Search ${activeTab}...`}
                      value={filters.search}
                      onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                )}
              </div>
              {activeTab === 'audit' && (
                <button
                  onClick={handleExportLogs}
                  className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap"
                >
                  <Download className="w-5 h-5" />
                  Export CSV
                </button>
              )}
              {(activeTab === 'policies' || activeTab === 'permissions') && (
                <button
                  onClick={() => openModal(activeTab === 'policies' ? 'addPolicy' : 'addPermission')}
                  className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
                >
                  <Plus className="w-5 h-5" />
                  Add {activeTab === 'policies' ? 'Policy' : 'Permission Group'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Content Based on Active Tab */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="p-6">
            <h2 className="text-2xl font-bold mb-6">Access Control Overview</h2>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between mb-2">
                  <Shield className="w-8 h-8 text-blue-600" />
                  <span className="text-3xl font-bold text-blue-600">{policyStats?.total || 0}</span>
                </div>
                <h3 className="text-sm font-medium text-gray-600">Total Policies</h3>
                <p className="text-xs text-gray-500 mt-1">{policyStats?.active || 0} active</p>
              </div>

              <div className="bg-green-50 p-6 rounded-lg border border-green-200">
                <div className="flex items-center justify-between mb-2">
                  <Key className="w-8 h-8 text-green-600" />
                  <span className="text-3xl font-bold text-green-600">{permissionGroups.length}</span>
                </div>
                <h3 className="text-sm font-medium text-gray-600">Permission Groups</h3>
                <p className="text-xs text-gray-500 mt-1">
                  {permissionGroups.filter(g => g.is_active).length} active
                </p>
              </div>

              <div className="bg-purple-50 p-6 rounded-lg border border-purple-200">
                <div className="flex items-center justify-between mb-2">
                  <Users className="w-8 h-8 text-purple-600" />
                  <span className="text-3xl font-bold text-purple-600">{roles.length}</span>
                </div>
                <h3 className="text-sm font-medium text-gray-600">Roles</h3>
                <p className="text-xs text-gray-500 mt-1">System roles defined</p>
              </div>

              <div className="bg-yellow-50 p-6 rounded-lg border border-yellow-200">
                <div className="flex items-center justify-between mb-2">
                  <FileText className="w-8 h-8 text-yellow-600" />
                  <span className="text-3xl font-bold text-yellow-600">{auditStats?.total || 0}</span>
                </div>
                <h3 className="text-sm font-medium text-gray-600">Audit Logs</h3>
                <p className="text-xs text-gray-500 mt-1">Total entries</p>
              </div>
            </div>

            {/* Policy Distribution */}
            {policyStats?.by_resource && (
              <div className="mb-8">
                <h3 className="text-lg font-semibold mb-4">Policies by Resource Type</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {Object.entries(policyStats.by_resource).map(([resource, count]) => (
                    <div key={resource} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                      <div className="text-2xl font-bold text-gray-900">{count}</div>
                      <div className="text-sm text-gray-600 capitalize">{resource}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recent Activity */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Recent Access Activity</h3>
              <div className="space-y-2">
                {auditLogs.slice(0, 5).map((log) => (
                  <div key={log.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center gap-3">
                      <Activity className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{log.description}</p>
                        <p className="text-xs text-gray-500">
                          {log.username || 'Unknown'} • {formatDate(log.created_at)}
                        </p>
                      </div>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(log.status)}`}>
                      {log.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Access Policies Tab */}
        {activeTab === 'policies' && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left p-4 font-semibold text-gray-700">Policy Name</th>
                  <th className="text-left p-4 font-semibold text-gray-700">Resource</th>
                  <th className="text-left p-4 font-semibold text-gray-700">Actions</th>
                  <th className="text-left p-4 font-semibold text-gray-700">Roles</th>
                  <th className="text-left p-4 font-semibold text-gray-700">Priority</th>
                  <th className="text-left p-4 font-semibold text-gray-700">Status</th>
                  <th className="text-left p-4 font-semibold text-gray-700">Operations</th>
                </tr>
              </thead>
              <tbody>
                {policiesLoading ? (
                  <tr>
                    <td colSpan="7" className="p-8">
                      <LoadingState message="Loading policies..." />
                    </td>
                  </tr>
                ) : filteredPolicies.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="p-8">
                      <EmptyState
                        icon={Shield}
                        title="No policies found"
                        message="Try adjusting your search or create a new policy"
                      />
                    </td>
                  </tr>
                ) : (
                  filteredPolicies.map((policy) => (
                    <tr key={policy.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="p-4">
                        <div>
                          <p className="font-medium text-gray-900">{policy.name}</p>
                          <p className="text-sm text-gray-500">{policy.description}</p>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getResourceTypeBadge(policy.resource_type)}`}>
                          {policy.resource_type}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex flex-wrap gap-1">
                          {policy.actions.map((action, idx) => (
                            <span key={idx} className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded text-xs">
                              {action}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex flex-wrap gap-1">
                          {policy.role_names?.map((role, idx) => (
                            <span key={idx} className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs">
                              {role}
                            </span>
                          ))}
                          {!policy.role_names?.length && <span className="text-gray-400 text-sm">No roles</span>}
                        </div>
                      </td>
                      <td className="p-4">
                        <span className="text-gray-900 font-medium">{policy.priority}</span>
                      </td>
                      <td className="p-4">
                        {policy.is_active ? (
                          <span className="flex items-center gap-1 text-green-600">
                            <CheckCircle className="w-4 h-4" />
                            Active
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-gray-400">
                            <XCircle className="w-4 h-4" />
                            Inactive
                          </span>
                        )}
                      </td>
                      <td className="p-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => openModal('editPolicy', policy)}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openModal('deletePolicy', policy)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Permission Groups Tab */}
        {activeTab === 'permissions' && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left p-4 font-semibold text-gray-700">Group Name</th>
                  <th className="text-left p-4 font-semibold text-gray-700">Module</th>
                  <th className="text-left p-4 font-semibold text-gray-700">Description</th>
                  <th className="text-left p-4 font-semibold text-gray-700">Permissions</th>
                  <th className="text-left p-4 font-semibold text-gray-700">Status</th>
                  <th className="text-left p-4 font-semibold text-gray-700">Operations</th>
                </tr>
              </thead>
              <tbody>
                {permissionsLoading ? (
                  <tr>
                    <td colSpan="6" className="p-8">
                      <LoadingState message="Loading permission groups..." />
                    </td>
                  </tr>
                ) : filteredPermissions.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="p-8">
                      <EmptyState
                        icon={Key}
                        title="No permission groups found"
                        message="Try adjusting your search or create a new permission group"
                      />
                    </td>
                  </tr>
                ) : (
                  filteredPermissions.map((group) => (
                    <tr key={group.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="p-4">
                        <p className="font-medium text-gray-900">{group.name}</p>
                      </td>
                      <td className="p-4">
                        <span className="bg-indigo-100 text-indigo-700 px-2 py-1 rounded text-sm font-medium capitalize">
                          {group.module}
                        </span>
                      </td>
                      <td className="p-4">
                        <p className="text-sm text-gray-600">{group.description}</p>
                      </td>
                      <td className="p-4">
                        <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-sm font-medium">
                          {group.permission_count} permissions
                        </span>
                      </td>
                      <td className="p-4">
                        {group.is_active ? (
                          <span className="flex items-center gap-1 text-green-600">
                            <CheckCircle className="w-4 h-4" />
                            Active
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-gray-400">
                            <XCircle className="w-4 h-4" />
                            Inactive
                          </span>
                        )}
                      </td>
                      <td className="p-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => openModal('viewPermission', group)}
                            className="text-gray-600 hover:text-gray-800"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openModal('editPermission', group)}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openModal('deletePermission', group)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Audit Logs Tab */}
        {activeTab === 'audit' && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left p-4 font-semibold text-gray-700">Timestamp</th>
                  <th className="text-left p-4 font-semibold text-gray-700">User</th>
                  <th className="text-left p-4 font-semibold text-gray-700">Action</th>
                  <th className="text-left p-4 font-semibold text-gray-700">Resource</th>
                  <th className="text-left p-4 font-semibold text-gray-700">Description</th>
                  <th className="text-left p-4 font-semibold text-gray-700">IP Address</th>
                  <th className="text-left p-4 font-semibold text-gray-700">Status</th>
                </tr>
              </thead>
              <tbody>
                {logsLoading ? (
                  <tr>
                    <td colSpan="7" className="p-8">
                      <LoadingState message="Loading audit logs..." />
                    </td>
                  </tr>
                ) : filteredLogs.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="p-8">
                      <EmptyState
                        icon={FileText}
                        title="No audit logs found"
                        message="Try adjusting your filters to see more results"
                      />
                    </td>
                  </tr>
                ) : (
                  filteredLogs.map((log) => (
                    <tr key={log.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="p-4">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Clock className="w-4 h-4" />
                          {formatDate(log.created_at)}
                        </div>
                      </td>
                      <td className="p-4">
                        <div>
                          <p className="font-medium text-gray-900">{log.username || 'Unknown'}</p>
                          <p className="text-xs text-gray-500">{log.user_full_name}</p>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded text-xs font-medium capitalize">
                          {log.action_type}
                        </span>
                      </td>
                      <td className="p-4">
                        <div>
                          <p className="text-sm font-medium text-gray-900 capitalize">{log.resource_type}</p>
                          {log.resource_id && (
                            <p className="text-xs text-gray-500">{log.resource_id}</p>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        <p className="text-sm text-gray-600">{log.description}</p>
                      </td>
                      <td className="p-4">
                        <p className="text-sm text-gray-600 font-mono">{log.ip_address || 'N/A'}</p>
                      </td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${getStatusBadge(log.status)}`}>
                          {log.status}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modals */}
      {showModal && (modalType === 'deletePolicy' || modalType === 'deletePermission') && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-2xl font-bold mb-4 text-red-600">Confirm Deletion</h2>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this {modalType === 'deletePolicy' ? 'policy' : 'permission group'}?
              This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={closeModal}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {showModal && modalType === 'viewPermission' && selectedItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold">Permission Group Details</h2>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-gray-700">Name</h3>
                <p className="text-gray-900">{selectedItem.name}</p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-700">Module</h3>
                <p className="text-gray-900 capitalize">{selectedItem.module}</p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-700">Description</h3>
                <p className="text-gray-900">{selectedItem.description}</p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-700 mb-2">Permissions</h3>
                <div className="space-y-2">
                  {selectedItem.permissions?.map((perm, idx) => (
                    <div key={idx} className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                      <p className="font-medium text-gray-900">{perm.name}</p>
                      <p className="text-sm text-gray-600">{perm.description}</p>
                      <p className="text-xs text-gray-500 mt-1 font-mono">{perm.id}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccessControl;
